import { api } from '@core/api/http';
import { environment } from '@env/environment';

/**
 * The assistant's backend - a port of `AoaiService`.
 *
 * `ask` was an Observable of one value, so it becomes a Promise. `askStream`
 * was an Observable of many, so it becomes a callback subscription that
 * returns its own unsubscribe - the same contract, without pulling a stream
 * library into
 * the React app for a single use.
 */

export type Role = 'user' | 'assistant';
export type Phase = 'thinking' | 'typing' | 'done';

export interface AgentMessageDto {
  createdAt: string;
  role: Role | string;
  text: string;
}

export interface AgentAskRequest {
  message: string;
  threadId?: string;
  agentId?: string;
  // add any extra fields you pass to your backend here
}

export interface AgentAskResponse {
  success: boolean;
  threadId: string;
  runId: string;
  status: string;
  messages: AgentMessageDto[];
  message?: string; // error payloads
}

export interface AgentStreamEvent {
  type: 'thread' | 'delta' | 'done';
  threadId?: string;
  delta?: string;
}

const baseUrl = `${environment.apiBaseUrl}/api/v1`;

/** One-shot (non-streaming) call. */
export async function ask(request: AgentAskRequest): Promise<AgentAskResponse> {
  const response = await api.post<AgentAskResponse>(`${baseUrl}/agent/ask`, request);

  return {
    ...response,
    messages: (response.messages ?? []).map((message) => ({
      ...message,
      role: String(message.role).toLowerCase() as Role
    }))
  };
}

export interface StreamHandlers {
  next: (event: AgentStreamEvent) => void;
  error: (error: unknown) => void;
  complete: () => void;
}

/**
 * Streaming API.
 *
 * Expects NDJSON/SSE-like lines from `${baseUrl}/agent/ask/stream`:
 *   data: {"threadId":"thd_123"}\n
 *   data: {"delta":"Hello"}\n
 *   data: {"delta":" world"}\n
 *   [DONE]\n
 *
 * NOTE - worth a look: this call sends no Authorization header. The token
 * attachment in core/api/http.ts only ever sees requests made
 * through `HttpClient`, and this one was written against the raw `fetch` API,
 * so it bypassed the interceptor entirely. Whether that is intentional (an
 * anonymous streaming endpoint) or a latent bug is a backend question, so the
 * behaviour is preserved rather than silently changed. To attach a token,
 * replace the `fetch` below with the `http()` wrapper using
 * `responseType: 'response'`.
 *
 * Returns an unsubscribe function that aborts the request in flight.
 */
export function askStream(request: AgentAskRequest, handlers: StreamHandlers): () => void {
  const controller = new AbortController();
  let settled = false;

  const finish = () => {
    if (settled) {
      return;
    }

    settled = true;
    handlers.next({ type: 'done' });
    handlers.complete();
  };

  void (async () => {
    try {
      const response = await fetch(`${baseUrl}/agent/ask/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: controller.signal
      });

      if (!response.ok || !response.body) {
        throw new Error(`${response.status} ${response.statusText}`);
      }

      // Optional: capture the thread id from a response header if present.
      const headerThread = response.headers.get('x-thread-id');

      if (headerThread) {
        handlers.next({ type: 'thread', threadId: headerThread });
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // Recursing once per chunk would grow the stack for
      // the length of the stream; a loop does the same work flat.
      for (;;) {
        const { done, value } = await reader.read();

        if (done) {
          finish();
          return;
        }

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        // The last element is a partial line - hold it back for the next chunk.
        buffer = lines.pop() ?? '';

        for (const raw of lines) {
          const line = raw.trim();

          if (!line) {
            continue;
          }

          if (line === '[DONE]') {
            finish();
            controller.abort();
            return;
          }

          const jsonStr = line.startsWith('data:') ? line.slice(5).trim() : line;

          try {
            const parsed = JSON.parse(jsonStr);

            if (parsed.threadId) {
              handlers.next({ type: 'thread', threadId: parsed.threadId });
            }

            if (parsed.delta) {
              handlers.next({ type: 'delta', delta: parsed.delta });
            }

            if (parsed.done) {
              finish();
              controller.abort();
              return;
            }
          } catch {
            // Not JSON - the server is emitting plain text tokens.
            handlers.next({ type: 'delta', delta: line });
          }
        }
      }
    } catch (error) {
      // An abort is the caller unsubscribing, not a failure.
      if (controller.signal.aborted) {
        return;
      }

      if (!settled) {
        settled = true;
        handlers.error(error);
      }
    }
  })();

  return () => {
    settled = true;
    controller.abort();
  };
}
